import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';

import {
  HOUSEHOLD_STATUS_ACTIVE,
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
} from 'common/householdConstants';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_REQUESTED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_STARTED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_COMPLETED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_PAID,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_DELIVERED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_ACCEPTED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_LEASE_SIGNED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_ALL,
} from 'common/dataTableConstants';

import {
  SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_BUYERS_AGENT,
} from 'api/actionTypes';

import SrHouseholdDashboardCard from 'client/components/SrHouseholdDashboardCard';
import SrAdminMergeHouseholdsModal from 'client/components/SrAdminMergeHouseholdsModal';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrHouseholdUpdateBuyersAgentForm from 'client/components/SrHouseholdUpdateBuyersAgentForm';
import SrHouseholdPendingLandlordModal from 'client/components/SrHouseholdPendingLandlordModal';
import SrHouseholdLeaseSignedModal from 'client/components/SrHouseholdLeaseSignedModal';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import {
  dataTable,
  actionDataTableLoadNextPage,
  actionDataTableLoadItems,
  actionDataTableReset,
} from 'client/helpers/dataTables';
import {
  makeProxiedS3FilePath,
} from 'client/lib/fileUploader';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';

function getDataTableSettingsFromProps({ viewId, userId }) {
  let dataTableSettings = {};
  switch (viewId) {
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_REQUESTED: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_STARTED: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_STARTED,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_COMPLETED: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_PAID: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_PAID,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_DELIVERED: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_ACCEPTED: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_LEASE_SIGNED: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
        filter: {
          'households.status': HOUSEHOLD_STATUS_ACTIVE,
        },
        customParams: {
          householdDashboard: {
            applicationStatus: HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_ALL: {
      dataTableSettings = {
        sort: {
          'households.lastUpdatedDate': 'desc',
        },
      };
    }
  }

  // HACK(@d-oliveros): Create new viewIds using the user's ID.
  if (userId) {
    dataTableSettings.viewId = `${viewId}-BY-USER-ID-${userId}`;
    dataTableSettings.filters = {};
    dataTableSettings.customParams = {
      householdDashboard: {
        userId,
      },
    };
  }
  return dataTableSettings;
}

@dataTable((props) => ({
  dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
  viewId: props.viewId,
  ...getDataTableSettingsFromProps(props),
}))
@branch({
  appUrl: ['env', 'APP_URL'],
  currentUser: ['currentUser'],
})
@withInfiniteScroll({
  offset: 32,
})
class SrDataTableHouseholdDashboard extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    householdAdminsList: PropTypes.array.isRequired,
    appUrl: PropTypes.string.isRequired,
    reloadDataTableHousehold: PropTypes.func,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      mergeHouseholdsIsOpen: false,
      photoLinks: null,
      additionalDocuments: null,
      pdfFiles: null,
      esaPetPhotoLinks: null,
      photoRotationMapping: {},
      leaseSignedModalHouseholdId: null,
      leaseSignedModalListingId: null,
      leaseSignedModalRentPrice: null,
      pendingLandlordModalHouseholdId: null,
      pendingLandlordModalListingId: null,
    };

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dataTableApiState, dispatch, viewId } = this.props;
    if (!dataTableIsFullyLoaded && !dataTableApiState.inProgress) {
      dispatch(actionDataTableLoadNextPage, {
        dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
        viewId: viewId,
        count: 5,
      });
    }
  }

  _onRefreshButtonClick = () => {
    this.props.dispatch(actionDataTableReset, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
    });
  }

  _onPhotosModalOpenClick = ({
    photoLinks,
    additionalDocuments,
    esaPetPhotoLinks,
    pdfFiles,
  }) => {
    this.setState({
      photoLinks,
      additionalDocuments,
      esaPetPhotoLinks,
      pdfFiles,
    });
  }

  _onPhotosModalCloseClick = () => {
    this.setState({
      photoLinks: null,
      additionalDocuments: null,
      esaPetPhotoLinks: null,
      pdfFiles: null,
    });
  }

  _mergeHouseholdsToggle = () => {
    this.setState({
      mergeHouseholdsIsOpen: !this.state.mergeHouseholdsIsOpen,
    });
  }

  _openBuyersAgentModal = (householdListing) => {
    this.setState({
      buyersAgentModalHouseholdListing: householdListing,
    });
  }

  _closeBuyersAgentModal = () => {
    this.setState({
      buyersAgentModalHouseholdListing: null,
    });
  }

  _onUpdateBuyersAgentFormSubmit = (formValues) => {
    const { dispatch } = this.props;
    const { buyersAgentModalHouseholdListing } = this.state;

    if (formValues.hasBuyersAgent === false) {
      formValues.buyersAgentName = null;
      formValues.buyersAgentPhone = null;
      formValues.buyersAgentEmail = null;
    }

    this._closeBuyersAgentModal();

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_BUYERS_AGENT,
      payload: {
        householdId: buyersAgentModalHouseholdListing.householdListingHouseholdId,
        listingId: buyersAgentModalHouseholdListing.listing.id,
        updates: formValues,
      },
    }).then(() => {
      return this._reloadDataTableHousehold({
        householdId: buyersAgentModalHouseholdListing.householdListingHouseholdId,
      });
    }).catch((error) => {
      log.error(error);
    });
  }

  _reloadDataTableHousehold({ householdId }) {
    const { dispatch, reloadDataTableHousehold } = this.props;
    return dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
      viewId: `household-id-${householdId}`,
      filter: {
        'households.id': householdId,
      },
      count: 1,
    })
      .then(() => {
        if (reloadDataTableHousehold) {
          reloadDataTableHousehold(householdId);
        }
      });
  }

  _onRotatePhoto = (e) => {
    preventDefaultPropagation(e);
    const photoLink = e.target.dataset.photoLink;
    this.setState({
      photoRotationMapping: {
        ...this.state.photoRotationMapping,
        [photoLink]: (this.state.photoRotationMapping[photoLink] || 0) + 1,
      },
    });
  }

  _onHouseholdLeaseSignedStatusUpdate = ({ householdId, listingId }) => {
    const household = this.props.items.find((item) => item.id === householdId);
    const listingApplication = household.listingApplications.find(({ listing }) => listing.id === listingId);

    this.setState({
      leaseSignedModalHouseholdId: householdId,
      leaseSignedModalListingId: listingId,
      leaseSignedModalRentPrice: (
        listingApplication.householdListingRentPrice || listingApplication.listing.price
      ),
    });
  }

  _onHouseholdLeaseSignedModalClose = ({ householdId }) => {
    this.setState({
      leaseSignedModalHouseholdId: null,
      leaseSignedModalListingId: null,
      leaseSignedModalRentPrice: null,
    });
    this._reloadDataTableHousehold({
      householdId,
    });
  }

  _onHouseholdPendingLandlordStatusUpdate = ({ householdId, listingId }) => {
    this.setState({
      pendingLandlordModalHouseholdId: householdId,
      pendingLandlordModalListingId: listingId,
    });
  }

  _onHouseholdPendingLandlordModalClose = ({ householdId }) => {
    this.setState({
      pendingLandlordModalHouseholdId: null,
      pendingLandlordModalListingId: null,
    });
    this._reloadDataTableHousehold({
      householdId,
    });
  }

  render() {
    const {
      items,
      dispatch,
      dataTableApiState,
      onInfiniteScrollWrapperRef,
      householdAdminsList,
      appUrl,
      reloadDataTableHousehold,
      currentUser,
    } = this.props;

    const {
      photoLinks,
      additionalDocuments,
      esaPetPhotoLinks,
      photoRotationMapping,
      pdfFiles,
      buyersAgentModalHouseholdListing,
      leaseSignedModalHouseholdId,
      leaseSignedModalListingId,
      leaseSignedModalRentPrice,
      pendingLandlordModalHouseholdId,
      pendingLandlordModalListingId,
    } = this.state;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <button
          className='floatButton refresh'
          type='button'
          onClick={this._onRefreshButtonClick}
        />
        <button
          className='floatButton merge'
          type='button'
          onClick={this._mergeHouseholdsToggle}
        />
        <SrAdminMergeHouseholdsModal
          isOpen={this.state.mergeHouseholdsIsOpen}
          onClose={this._mergeHouseholdsToggle}
        />
        {items.map((item) => (
          <SrHouseholdDashboardCard
            key={item.id}
            appUrl={appUrl}
            currentUser={currentUser}
            household={item}
            dispatch={dispatch}
            onPhotosModalOpenClick={this._onPhotosModalOpenClick}
            openBuyersAgentModal={this._openBuyersAgentModal}
            householdAdminsList={householdAdminsList}
            reloadDataTableHousehold={reloadDataTableHousehold}
            onHouseholdLeaseSignedStatusUpdate={this._onHouseholdLeaseSignedStatusUpdate}
            onHouseholdPendingLandlordStatusUpdate={this._onHouseholdPendingLandlordStatusUpdate}
            leaseStartDate={item.leaseStartDate}
          />
        ))}
        {photoLinks && (
          <Portal>
            <div className='darkModal householdPhotosModal' onClick={this._onPhotosModalCloseClick}>
              {photoLinks.map((s3PhotoLink) => {
                const photoLink = makeProxiedS3FilePath(s3PhotoLink, appUrl);
                return (
                  <div
                    className='applicationPhotoContainer'
                    key={photoLink}
                    onClick={preventDefaultPropagation}
                  >
                    {esaPetPhotoLinks.includes(photoLink) && (
                      <span className='photoLabel'>
                        ESA
                      </span>
                    )}
                    {!photoLink.toLowerCase().includes('.pdf') && (
                      <img
                        src={photoLink}
                        alt={photoLink}
                        style={{ transform: `rotate(${(photoRotationMapping[photoLink] || 0) * 90}deg)` }}
                      />
                    )}
                    {!photoLink.toLowerCase().includes('.pdf') && (
                      <div className='rotateButton' data-photo-link={photoLink} onClick={this._onRotatePhoto}>
                        rotate
                      </div>
                    )}
                    {photoLink.toLowerCase().includes('.pdf') && (
                      <iframe
                        title={photoLink}
                        src={`${appUrl}/pdf-viewer?pdfUrl=${photoLink}`}
                        style={{ width: '75vw', height: 700 }}
                      />
                    )}
                  </div>
                );
              })}
              <div className='section'>
                <span className='title'>
                  {`${[...(additionalDocuments || []), ...(pdfFiles || [])].length === 0
                    ? 'No '
                    : ''}Additional Documents`
                  }
                </span>
                {additionalDocuments.map((additionalDocumentLink) => (
                  <div className='applicationPhotoContainer' key={additionalDocumentLink}>
                    <SrS3FileDisplay
                      url={additionalDocumentLink}
                      maxHeight='700px'
                    />
                  </div>
                ))}
                <div>
                  {pdfFiles.map((pdfFile) => (
                    <div key={pdfFile} className='applicationPhotoContainer'>
                      <SrS3FileDisplay
                        url={pdfFile}
                        maxHeight='700px'
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Portal>
        )}
        {buyersAgentModalHouseholdListing && (
          <SrDarkModal onClose={this._closeBuyersAgentModal}>
            <SrModalContent>
              <div className='formThemeAdmin'>
                <SrHouseholdUpdateBuyersAgentForm
                  householdListing={buyersAgentModalHouseholdListing}
                  onSubmit={this._onUpdateBuyersAgentFormSubmit}
                />
              </div>
            </SrModalContent>
          </SrDarkModal>
        )}
        {leaseSignedModalHouseholdId && leaseSignedModalListingId && items && items.length && (
          <SrHouseholdLeaseSignedModal
            householdId={leaseSignedModalHouseholdId}
            listingId={leaseSignedModalListingId}
            rentPrice={leaseSignedModalRentPrice}
            onClose={this._onHouseholdLeaseSignedModalClose}
            dispatch={dispatch}
            household={items.find((item) => item.id === leaseSignedModalHouseholdId)}
          />
        )}
        {pendingLandlordModalHouseholdId && pendingLandlordModalListingId && items && items.length && (
          <SrHouseholdPendingLandlordModal
            currentUser={currentUser}
            householdId={pendingLandlordModalHouseholdId}
            listingId={pendingLandlordModalListingId}
            household={items.find((item) => item.id === pendingLandlordModalHouseholdId)}
            onClose={this._onHouseholdPendingLandlordModalClose}
            dispatch={dispatch}
          />
        )}
        {dataTableApiState.inProgress && (
          <div className='loading-ellipses left' />
        )}
        {dataTableApiState.error && (
          <div>ERROR: { dataTableApiState.error }</div>
        )}
      </div>
    );
  }
}

SrDataTableHouseholdDashboard.displayName = 'SrDataTableHouseholdDashboard';

export default SrDataTableHouseholdDashboard;
